import { Button, CircularProgress } from '@mui/material';
import CopyText from 'js/components/elements/CopyText';
import { ROUTE_API_KEYS } from 'js/constants/pathnames';
import {
  getUsableApiKey,
  getDatasetDataUrl,
  sanitizeApiKeyURL,
  getDatasetSyncPayloadString,
  getDatasetFeedUrlSampleRequest,
} from 'js/lib/URLUtils';
import { flatten, isEmpty, isNil, map } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';

type ApiKey = {
  id: string;
  name: string | null;
  api_key: string;
  url: string;
  status: string;
};

type DatasetData = {
  id: number;
  nexset_api_config?: {
    url: string;
  };
  sync_api_config?: {
    params?: Array<{
      param: string;
      default?: unknown;
    }>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type RenderFeedApiViewConfig = {
  isSyncable: boolean;
  data: DatasetData;
  apiKeys: Record<string, ApiKey>;
  isLoading: { dataFeedApiKey: boolean };
  generateNexsetDataLink: () => void;
  routePrefix: string;
};

type RenderFeedApiViewProps = {
  config: RenderFeedApiViewConfig;
};

export const renderFeedApiView = (props: RenderFeedApiViewProps) => {
  const { config } = props;
  const { data, apiKeys, isLoading, isSyncable, routePrefix, generateNexsetDataLink } = config;

  let datasetSyncInfo: React.ReactNode = '';

  if (isSyncable === true) {
    let dataFeedUrlView: React.ReactNode = null;
    let nexsetApiKey: string | null = null;

    if (isEmpty(apiKeys) || isLoading.dataFeedApiKey === true) {
      dataFeedUrlView = (
        <div className="detail-col">
          <div className="detail-key">Data Feed API</div>
          <div className="detail-value" style={{ marginTop: 'var(--spacing-sm)' }}>
            <Button
              color="primary"
              variant="ghost"
              onClick={generateNexsetDataLink}
              disabled={isLoading.dataFeedApiKey}
              startIcon={isLoading.dataFeedApiKey ? <CircularProgress size={14} /> : undefined}
            >
              {isLoading.dataFeedApiKey ? 'Loading...' : 'Show Data Feed URL'}
            </Button>
          </div>
        </div>
      );
    } else {
      nexsetApiKey = getUsableApiKey(apiKeys);
      let dataFeedUrl =
        'nexset_api_config' in data
          ? data.nexset_api_config.url
          : getDatasetDataUrl(data.id, nexsetApiKey);
      let sanitizedFeedUrl = sanitizeApiKeyURL(dataFeedUrl);
      let dataFeedPayload = getDatasetSyncPayloadString(data.sync_api_config);

      let feedUrlContent = getDatasetFeedUrlSampleRequest(
        dataFeedUrl,
        sanitizedFeedUrl,
        dataFeedPayload,
      );

      let apiKeyView: React.ReactNode = isEmpty(apiKeys)
        ? ''
        : flatten(
            map(apiKeys, (apiKey) => [
              <Link
                key={apiKey.id}
                to={`${routePrefix}${ROUTE_API_KEYS}/${apiKey.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {!isNil(apiKey.name) ? apiKey.name : apiKey.id}
              </Link>,
              ', ',
            ]),
          ).slice(0, -1);

      dataFeedUrlView = (
        <div className="detail-col">
          <div className="detail-key">Data Feed API</div>
          <div className="detail-value" style={{ marginTop: 'var(--spacing-sm)' }}>
            <CopyText
              baseText={feedUrlContent.sample}
              displayText={feedUrlContent.sanitizedSample}
              iconSize="small"
              iconButtonSx={{
                border: '1px solid var(--color-greyscale-200)',
                borderRadius: '16px',
                padding: '2px',
                width: '32px',
                height: '32px',
              }}
            />
          </div>
          <div className="detail-key">API Keys</div>
          <div className="detail-value">{apiKeyView}</div>
        </div>
      );
    }

    datasetSyncInfo = <div className="detail-row single-col">{dataFeedUrlView}</div>;
  }

  return datasetSyncInfo;
};
